import type { PublicNotice } from "./api";

export type NoticeSection = { heading: string; paragraphs: string[]; list?: string[] };

/** The address of an agency's candidate privacy notice. */
export const noticeUrl = (tenantId: string) => `${window.location.origin}/notice/${tenantId}`;

const years = (months: number) => (months % 12 === 0 ? `${months / 12} year${months === 12 ? "" : "s"}` : `${months} months`);

/**
 * The candidate privacy notice (UK GDPR articles 13 and 14) for an agency, written for people
 * whose details a recruiter found or was sent. A template: agencies should check it fits how they work.
 */
export function noticeSections(n: PublicNotice): NoticeSection[] {
  const contact = n.contact ? `Contact us about it at ${n.contact}.` : "Reply to the recruiter who contacted you.";
  return [
    {
      heading: "Who we are",
      paragraphs: [
        `${n.agency} is a recruitment agency. We decide how your details are used, which makes us the controller of them under UK data protection law. ${contact}`,
        ...(n.icoNumber ? [`We're registered with the Information Commissioner's Office, number ${n.icoNumber}.`] : []),
      ],
    },
    {
      heading: "What we hold",
      paragraphs: ["Only what helps us find you work:"],
      list: [
        "your name and contact details",
        "your work history, skills, qualifications and CV",
        "where you'd like to work, your salary expectations and notice period",
        "notes of our conversations with you, and the roles we've put you forward for and how they went",
      ],
    },
    {
      heading: "Where it came from",
      paragraphs: [
        "From you, if you applied or sent us your CV. Otherwise from where you made it public for this purpose, such as your LinkedIn profile, a job board or a professional directory, or from someone who recommended you.",
      ],
    },
    {
      heading: "Why we use it, and on what basis",
      paragraphs: [
        "To tell you about jobs that might suit you and, only with your agreement each time, to introduce you to employers for a specific role.",
        "Our lawful basis is legitimate interests: ours in running a recruitment business, and yours in hearing about suitable work. When you ask us to put you forward for a job, we're also taking steps you asked for. You can object at any time, and we'll stop.",
        "We don't make decisions about you by automated means alone.",
      ],
    },
    {
      heading: "Who sees it",
      paragraphs: [
        "Employers we introduce you to, and only for roles you've agreed to. The companies that provide our software and email, which hold it on our behalf under contract, in the UK or the European Economic Area. We don't sell it and we don't share it with anyone else unless the law requires us to.",
      ],
    },
    {
      heading: "How long we keep it",
      paragraphs: [
        `We review your details if we haven't been in touch for ${years(n.retentionMonths)}, and delete them unless you'd like us to keep them. If you ask us to delete them sooner, we will.`,
      ],
    },
    {
      heading: "Your rights",
      paragraphs: ["You can ask us to:"],
      list: [
        "tell you what we hold about you and send you a copy",
        "correct anything that's wrong",
        "delete it, or stop using it for a while",
        "stop contacting you about jobs",
        "give it to you in a format another organisation can use",
      ],
    },
    {
      heading: "Asking, or complaining",
      paragraphs: [
        `${contact} We'll answer within a month. You don't have to give us your details, but without them we can't put you forward for jobs.`,
        "If you're unhappy with how we've handled your details, you can complain to the Information Commissioner's Office at ico.org.uk or on 0303 123 1113.",
      ],
    },
  ];
}

/** A short email to a candidate pointing to the notice, opened in the recruiter's own mail app. */
export function noticeEmail(candidate: { fullName: string; email: string }, agency: string, recruiter: string, url: string): string {
  const first = candidate.fullName.replace(/^(dr|mr|mrs|ms|miss|prof)\.?\s+/i, "").split(" ")[0];
  const subject = `Your details with ${agency}`;
  const body = [
    `Hi ${first},`,
    "",
    `I'm a recruiter at ${agency}, and I've added your details to our records so I can tell you about roles that might suit you.`,
    "",
    `This explains what we hold, why, and your rights, including asking us to delete it: ${url}`,
    "",
    "If you'd rather not hear from us, just reply and say so.",
    "",
    recruiter,
  ].join("\n");
  return `mailto:${candidate.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
